import { appError, err, ok, } from '@orbit-mind/shared-types';
import { createDocument, createNode } from './factories';
import { orderAtEnd } from './ordering';
import { richTextFromPlain, toPlainText } from './richText';
import { childrenOf } from './tree';
export const TEXT_IMPORT_LIMIT = 50_000;
export const TEXT_IMPORT_NODE_LIMIT = 200;
/** Deterministic extraction. Every note retains its original line; nothing is invented. */
export function mapFromText(input) {
    const lines = input.text
        .replace(/\r\n?/g, '\n')
        .split('\n')
        .map((text, index) => ({ text, line: index + 1 }))
        .filter(({ text }) => text.trim() !== '');
    if (input.text.length > TEXT_IMPORT_LIMIT || lines.length > TEXT_IMPORT_NODE_LIMIT) {
        return err(appError('VALIDATION_FAILED', 'Use up to 50,000 characters and 200 non-empty lines.', {
            retryable: false,
        }));
    }
    if (lines.length === 0 || input.title.trim() === '')
        return err(appError('VALIDATION_FAILED', 'Add a title and some text.', { retryable: false }));
    const base = createDocument({
        mapId: input.mapId,
        rootNodeId: input.nodeId(),
        ownerId: input.ownerId,
        workspaceId: input.workspaceId,
        now: input.now,
        title: input.title.trim().slice(0, 500),
    });
    const nodes = { ...base.nodes };
    const stack = [{ depth: 0, id: base.map.rootNodeId }];
    const outline = [];
    for (const entry of lines) {
        const heading = /^(#{1,6})\s+(.+)/.exec(entry.text.trim());
        const depth = heading?.[1]?.length ?? 1;
        const indent = (/^[\t ]*/.exec(entry.text)?.[0] ?? '').replace(/\t/g, '  ').length;
        if (heading !== null) {
            outline.length = 0;
            while (stack.length > 1 && (stack[stack.length - 1]?.depth ?? 0) >= depth)
                stack.pop();
        }
        else {
            while (outline.length > 0 && (outline[outline.length - 1]?.indent ?? 0) >= indent)
                outline.pop();
        }
        const parentId = (heading === null ? outline[outline.length - 1]?.id : undefined) ??
            stack[stack.length - 1]?.id ??
            base.map.rootNodeId;
        const text = (heading?.[2] ?? entry.text.trim().replace(/^[-*•]\s+/, '')).trim();
        const id = input.nodeId();
        const siblings = Object.values(nodes).filter((node) => node.parentId === parentId);
        const node = createNode({
            id,
            mapId: input.mapId,
            parentId,
            order: orderAtEnd(siblings.map((child) => child.order)),
            now: input.now,
            by: input.ownerId,
            text: text.length > 140 ? `${text.slice(0, 137)}…` : text,
        });
        nodes[id] = {
            ...node,
            notes: richTextFromPlain(entry.text),
            metadata: {
                source: {
                    label: input.source.trim() || input.title.trim(),
                    line: entry.line,
                    text: entry.text,
                },
            },
        };
        if (heading !== null)
            stack.push({ depth, id });
        else
            outline.push({ indent, id });
    }
    return ok({ ...base, nodes });
}
export function studyCards(document) {
    return Object.values(document.nodes)
        .filter((node) => !node.hidden)
        .flatMap((node) => {
        const children = childrenOf(document, node.id).filter((child) => !child.hidden);
        const notes = node.notes === undefined ? '' : toPlainText(node.notes).trim();
        // Avoid a card that asks a leaf to repeat its own title.
        const answer = children.length > 0 ? children.map((child) => child.plainText).join('\n') : notes;
        if (answer === '' || answer === node.plainText)
            return [];
        const review = reviewState(node);
        return [{ nodeId: node.id, prompt: node.plainText, answer, dueAt: review.dueAt }];
    });
}
function reviewState(node) {
    const raw = node.metadata?.study;
    if (typeof raw !== 'object' || raw === null)
        return { dueAt: 0, repetitions: 0 };
    const value = raw;
    return {
        dueAt: typeof value.dueAt === 'number' && Number.isFinite(value.dueAt) ? value.dueAt : 0,
        repetitions: typeof value.repetitions === 'number' && Number.isFinite(value.repetitions)
            ? Math.max(0, Math.min(5, Math.floor(value.repetitions)))
            : 0,
    };
}
export function recordReview(node, remembered, now) {
    const repetitions = remembered ? Math.min(5, reviewState(node).repetitions + 1) : 0;
    const days = [0, 1, 3, 7, 14, 30][repetitions] ?? 1;
    return {
        ...node.metadata,
        study: {
            repetitions,
            reviewedAt: now,
            dueAt: now + (remembered ? days * 86_400_000 : 10 * 60_000),
        },
    };
}
export function taskNodes(document) {
    return Object.values(document.nodes).filter((node) => node.status !== undefined && node.status !== 'none' && node.status !== 'cancelled');
}
/** Calendar dates are stored at local noon, avoiding a day shift when displayed in this locale. */
export function parseTaskDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value))
        return null;
    const [year, month, day] = value.split('-').map(Number);
    if (year === undefined || month === undefined || day === undefined)
        return null;
    const date = new Date(year, month - 1, day, 12);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day)
        return null;
    return date.getTime();
}
/** Original material remains separate from edited notes and generated suggestions. */
export function originalSource(node) {
    const source = node?.metadata?.source;
    if (typeof source !== 'object' ||
        source === null ||
        Array.isArray(source) ||
        !('label' in source) ||
        !('text' in source) ||
        !('line' in source))
        return null;
    return typeof source.label === 'string' &&
        typeof source.text === 'string' &&
        typeof source.line === 'number'
        ? { label: source.label, text: source.text, line: source.line }
        : null;
}
/** Bounded hierarchy preview shared by local import screens. */
export function importOutline(document, limit = 30) {
    const rows = [];
    const pending = [{ id: document.map.rootNodeId, depth: 0 }];
    const seen = new Set();
    while (pending.length && rows.length < limit) {
        const item = pending.pop();
        if (item === undefined)
            break;
        if (seen.has(item.id))
            continue;
        seen.add(item.id);
        const node = document.nodes[item.id];
        if (!node)
            continue;
        rows.push({ id: item.id, text: node.plainText, depth: item.depth });
        for (const child of [...childrenOf(document, item.id)].reverse())
            pending.push({ id: child.id, depth: item.depth + 1 });
    }
    return rows;
}
//# sourceMappingURL=productivity.js.map